import React, { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Palette, Upload, RotateCcw, Save, Eye, Image as ImageIcon } from 'lucide-react'
import { getBranding, updateBranding } from '../../services/api'
import type { BrandingConfig } from '../../types'

const DEFAULT_BRANDING: Omit<BrandingConfig, 'university_id'> = {
  primary_color: '#EA6025',
  secondary_color: '#D4520E',
  accent_color: '#FB923C',
  institution_name: 'Academic Discovery Portal',
  header_text: 'Discover. Connect. Learn.',
  footer_text: 'Powered by SecureEmbed',
}

export default function Branding() {
  const queryClient = useQueryClient()

  const { data: branding, isLoading } = useQuery({
    queryKey: ['admin', 'branding'],
    queryFn: getBranding,
  })

  const [formData, setFormData] = useState<Omit<BrandingConfig, 'university_id'>>({
    primary_color: branding?.primary_color ?? DEFAULT_BRANDING.primary_color,
    secondary_color: branding?.secondary_color ?? DEFAULT_BRANDING.secondary_color,
    accent_color: branding?.accent_color ?? DEFAULT_BRANDING.accent_color,
    institution_name: branding?.institution_name ?? DEFAULT_BRANDING.institution_name,
    header_text: branding?.header_text ?? DEFAULT_BRANDING.header_text,
    footer_text: branding?.footer_text ?? DEFAULT_BRANDING.footer_text,
    logo_url: branding?.logo_url ?? '',
  })

  const [logoPreview, setLogoPreview] = useState<string | null>(branding?.logo_url ?? null)

  // Sync form state when branding loads
  React.useEffect(() => {
    if (branding) {
      setFormData({
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
        accent_color: branding.accent_color,
        institution_name: branding.institution_name,
        header_text: branding.header_text ?? '',
        footer_text: branding.footer_text ?? '',
        logo_url: branding.logo_url ?? '',
      })
      if (branding.logo_url) {
        setLogoPreview(branding.logo_url)
      }
    }
  }, [branding])

  const mutation = useMutation({
    mutationFn: (data: Partial<BrandingConfig>) => updateBranding(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'branding'] })
    },
  })

  const handleChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleLogoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        setLogoPreview(result)
        setFormData((prev) => ({ ...prev, logo_url: result }))
      }
      reader.readAsDataURL(file)
    }
  }, [])

  const handleSave = () => {
    mutation.mutate(formData)
  }

  const handleReset = () => {
    setFormData({ ...DEFAULT_BRANDING, logo_url: '' })
    setLogoPreview(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        setLogoPreview(result)
        setFormData((prev) => ({ ...prev, logo_url: result }))
      }
      reader.readAsDataURL(file)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Palette className="w-6 h-6 text-acad-500" />
            White-Label Branding
          </h1>
          <p className="text-gray-500 mt-1">Customize the portal appearance for your institution</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg
                       text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-acad-500 text-white rounded-lg
                       text-sm font-medium hover:bg-acad-600 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {mutation.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          Branding configuration saved successfully.
        </div>
      )}

      {mutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          Failed to save branding configuration. Please try again.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Form (left side) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Colors Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Colors</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="w-12 h-10 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm
                               focus:outline-none focus:ring-2 focus:ring-acad-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    className="w-12 h-10 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.secondary_color}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm
                               focus:outline-none focus:ring-2 focus:ring-acad-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={formData.accent_color}
                    onChange={(e) => handleChange('accent_color', e.target.value)}
                    className="w-12 h-10 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.accent_color}
                    onChange={(e) => handleChange('accent_color', e.target.value)}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm
                               focus:outline-none focus:ring-2 focus:ring-acad-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Logo Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Logo</h2>
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8
                         text-center hover:border-acad-400 transition-colors cursor-pointer"
            >
              {logoPreview ? (
                <div className="space-y-4">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-h-24 mx-auto object-contain"
                  />
                  <p className="text-sm text-gray-500">Drag and drop to replace, or click below</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-10 h-10 text-gray-300 mx-auto" />
                  <p className="text-sm text-gray-500">Drag and drop your logo here</p>
                  <p className="text-xs text-gray-400">or click to browse</p>
                </div>
              )}
              <label className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-gray-300
                                rounded-lg text-sm font-medium text-gray-700 bg-white
                                hover:bg-gray-50 transition-colors cursor-pointer">
                <ImageIcon className="w-4 h-4" />
                Browse Files
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Text Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Text</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institution Name</label>
                <input
                  type="text"
                  value={formData.institution_name}
                  onChange={(e) => handleChange('institution_name', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-acad-500"
                  placeholder="Your Institution Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Header Text</label>
                <input
                  type="text"
                  value={formData.header_text ?? ''}
                  onChange={(e) => handleChange('header_text', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-acad-500"
                  placeholder="Portal header tagline"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text</label>
                <input
                  type="text"
                  value={formData.footer_text ?? ''}
                  onChange={(e) => handleChange('footer_text', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-acad-500"
                  placeholder="Footer attribution text"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Live Preview (right side) */}
        <div className="lg:col-span-2">
          <div className="sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-gray-400" />
              Live Preview
            </h2>
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
              {/* Preview Header */}
              <div
                className="px-6 py-4"
                style={{ backgroundColor: formData.primary_color }}
              >
                <div className="flex items-center gap-3">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="h-8 object-contain" />
                  ) : (
                    <div className="w-8 h-8 bg-white/20 rounded" />
                  )}
                  <div>
                    <h3 className="text-white font-semibold text-sm">{formData.institution_name}</h3>
                    <p className="text-white/80 text-xs">{formData.header_text}</p>
                  </div>
                </div>
              </div>

              {/* Preview Nav */}
              <div
                className="px-6 py-2 flex gap-4 text-xs font-medium"
                style={{ backgroundColor: formData.secondary_color }}
              >
                <span className="text-white/90">Search</span>
                <span className="text-white/60">Browse</span>
                <span className="text-white/60">My Lists</span>
                <span className="text-white/60">Connections</span>
              </div>

              {/* Preview Content */}
              <div className="px-6 py-6 space-y-3">
                <div className="h-3 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-full" />
                <div className="h-3 bg-gray-100 rounded w-5/6" />
                <div className="mt-4 flex gap-2">
                  <span
                    className="px-3 py-1 rounded text-xs text-white font-medium"
                    style={{ backgroundColor: formData.accent_color }}
                  >
                    Button
                  </span>
                  <span className="px-3 py-1 rounded text-xs border border-gray-200 text-gray-500">
                    Secondary
                  </span>
                </div>
              </div>

              {/* Preview Footer */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-400 text-center">{formData.footer_text}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
